'use client';

export type KeyboardLayoutPreset = 'en-ru' | 'ru-en' | 'en-ua' | 'ua-en';

export type CaseMode = 'lowercase' | 'uppercase' | 'title-case' | 'sentence-case' | 'swap-case';

export type SeparatorMode = 'dash' | 'underscore';

export type LineBreakMode = 'lf' | 'crlf';

export type WrongLayoutCandidate = {
  preset: KeyboardLayoutPreset;
  label: string;
  output: string;
  score: number;
};

const EN_KEYS = "`qwertyuiop[]asdfghjkl;'zxcvbnm,./";
const RU_KEYS = 'ёйцукенгшщзхъфывапролджэячсмитьбю.';
const UA_KEYS = "'йцукенгшщзхїфівапролджєячсмитьбю.";

const CYRILLIC_TO_ASCII_MAP: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  ґ: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  є: 'ye',
  ж: 'zh',
  з: 'z',
  и: 'i',
  і: 'i',
  ї: 'yi',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

const INVISIBLE_UNICODE_PATTERN = /[\u200B-\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g;
const NON_BREAKING_SPACES_PATTERN = /[\u00A0\u2007\u202F]/g;

const SMART_QUOTES_MAP: Record<string, string> = {
  '\u2018': "'",
  '\u2019': "'",
  '\u201A': "'",
  '\u201B': "'",
  '\u2032': "'",
  '\u2035': "'",
  '\u201C': '"',
  '\u201D': '"',
  '\u201E': '"',
  '\u201F': '"',
  '\u2033': '"',
  '\u2036': '"',
  '\u2013': '-',
  '\u2014': '-',
  '\u2015': '-',
  '\u2212': '-',
  '\u2026': '...',
};

const KEYBOARD_PRESETS: Array<{
  id: KeyboardLayoutPreset;
  label: string;
  from: string;
  to: string;
}> = [
  { id: 'en-ru', label: 'EN -> RU', from: EN_KEYS, to: RU_KEYS },
  { id: 'ru-en', label: 'RU -> EN', from: RU_KEYS, to: EN_KEYS },
  { id: 'en-ua', label: 'EN -> UA', from: EN_KEYS, to: UA_KEYS },
  { id: 'ua-en', label: 'UA -> EN', from: UA_KEYS, to: EN_KEYS },
];

function buildKeyboardMap(from: string, to: string) {
  const map = new Map<string, string>();

  for (let index = 0; index < from.length; index += 1) {
    const source = from[index];
    const target = to[index];

    map.set(source, target);
    map.set(source.toUpperCase(), target.toUpperCase());
  }

  return map;
}

const keyboardMaps = KEYBOARD_PRESETS.reduce<Record<KeyboardLayoutPreset, Map<string, string>>>(
  (accumulator, preset) => {
    accumulator[preset.id] = buildKeyboardMap(preset.from, preset.to);
    return accumulator;
  },
  {} as Record<KeyboardLayoutPreset, Map<string, string>>,
);

function preserveCase(source: string, output: string) {
  if (!output) {
    return output;
  }

  if (source === source.toUpperCase() && source !== source.toLowerCase()) {
    return output.toUpperCase();
  }

  if (source[0] === source[0]?.toUpperCase() && source.slice(1) === source.slice(1).toLowerCase()) {
    return output.charAt(0).toUpperCase() + output.slice(1);
  }

  return output;
}

function stripDiacritics(value: string) {
  return value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
}

function toAsciiTranslit(value: string) {
  const withoutDiacritics = stripDiacritics(value);
  let output = '';

  for (const char of withoutDiacritics) {
    const lowerChar = char.toLowerCase();
    const mapped = CYRILLIC_TO_ASCII_MAP[lowerChar];

    if (typeof mapped === 'string') {
      output += preserveCase(char, mapped);
      continue;
    }

    if (/[\x00-\x7F]/.test(char)) {
      output += char;
    }
  }

  return output;
}

function collapseSeparatorRuns(value: string, separator: string) {
  const escaped = separator === '-' ? '\\-' : separator;
  return value
    .replace(new RegExp(`${escaped}{2,}`, 'g'), separator)
    .replace(new RegExp(`^${escaped}|${escaped}$`, 'g'), '');
}

function normalizeToSentenceCase(value: string) {
  const lower = value.toLocaleLowerCase();
  let shouldCapitalize = true;
  let output = '';

  for (const char of lower) {
    if (shouldCapitalize && /\p{L}/u.test(char)) {
      output += char.toLocaleUpperCase();
      shouldCapitalize = false;
      continue;
    }

    output += char;

    if (/[.!?]\s*$/.test(output.slice(-2)) || /[.!?]/.test(char)) {
      shouldCapitalize = true;
    }
  }

  return output;
}

function normalizeToTitleCase(value: string) {
  return value
    .toLocaleLowerCase()
    .replace(
      /\p{L}[\p{L}\p{M}\p{N}'’_-]*/gu,
      (token) => token.charAt(0).toLocaleUpperCase() + token.slice(1),
    );
}

function swapCase(value: string) {
  return Array.from(value)
    .map((char) => {
      if (char === char.toLocaleUpperCase()) {
        return char.toLocaleLowerCase();
      }
      return char.toLocaleUpperCase();
    })
    .join('');
}

export function convertKeyboardLayout(value: string, preset: KeyboardLayoutPreset) {
  const map = keyboardMaps[preset];
  return Array.from(value)
    .map((char) => map.get(char) ?? char)
    .join('');
}

export function fixWrongKeyboardLayout(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error('Paste some text first.');
  }

  const candidates: WrongLayoutCandidate[] = KEYBOARD_PRESETS.map((preset) => {
    const output = convertKeyboardLayout(value, preset.id);
    const latinCount = (output.match(/[A-Za-z]/g) ?? []).length;
    const cyrillicCount = (output.match(/[А-Яа-яЁёІіЇїЄєҐґ]/g) ?? []).length;
    const wordLikeCount = (output.match(/[A-Za-zА-Яа-яЁёІіЇїЄєҐґ]{2,}/g) ?? []).length;
    const score = latinCount + cyrillicCount + wordLikeCount * 2;

    return {
      preset: preset.id,
      label: preset.label,
      output,
      score,
    };
  }).sort((left, right) => right.score - left.score);

  return {
    best: candidates[0],
    alternatives: candidates.slice(1),
    all: candidates,
  };
}

export function translitToAscii(value: string) {
  return toAsciiTranslit(value);
}

export function convertTextCase(value: string, mode: CaseMode) {
  switch (mode) {
    case 'lowercase':
      return value.toLocaleLowerCase();
    case 'uppercase':
      return value.toLocaleUpperCase();
    case 'title-case':
      return normalizeToTitleCase(value);
    case 'sentence-case':
      return normalizeToSentenceCase(value);
    case 'swap-case':
      return swapCase(value);
  }
}

export function generateSlug(value: string, separatorMode: SeparatorMode) {
  const separator = separatorMode === 'underscore' ? '_' : '-';
  const ascii = toAsciiTranslit(value)
    .toLocaleLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, separator);

  return collapseSeparatorRuns(ascii, separator);
}

function splitFileName(value: string) {
  const trimmed = value.trim();
  const lastSlash = Math.max(trimmed.lastIndexOf('/'), trimmed.lastIndexOf('\\'));
  const fileName = lastSlash >= 0 ? trimmed.slice(lastSlash + 1) : trimmed;
  const lastDot = fileName.lastIndexOf('.');

  if (lastDot <= 0 || lastDot === fileName.length - 1) {
    return { base: fileName, extension: '' };
  }

  return {
    base: fileName.slice(0, lastDot),
    extension: fileName.slice(lastDot + 1),
  };
}

function cleanFileBase(value: string) {
  const ascii = toAsciiTranslit(value)
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9._ -]+/g, ' ')
    .replace(/[._\s-]+/g, '-');

  return collapseSeparatorRuns(ascii, '-');
}

export function cleanFileNames(value: string) {
  const lines = value.split(/\r\n?|\n/);
  return lines
    .map((line) => {
      if (!line.trim()) {
        return '';
      }

      const { base, extension } = splitFileName(line);
      const cleanedBase = cleanFileBase(base).replace(/[.\s-]+$/g, '') || 'file';
      const cleanedExtension = extension
        ? toAsciiTranslit(extension)
            .toLocaleLowerCase()
            .replace(/[^a-z0-9]+/g, '')
        : '';

      return cleanedExtension ? `${cleanedBase}.${cleanedExtension}` : cleanedBase;
    })
    .join('\n');
}

export function removeExtraSpaces(value: string) {
  return value
    .split(/\r\n?|\n/)
    .map((line) => line.replace(/[ \t]{2,}/g, ' ').trim())
    .join('\n');
}

export function normalizeLineBreaks(value: string, mode: LineBreakMode) {
  const normalized = value.replace(/\r\n?/g, '\n');
  return mode === 'crlf' ? normalized.replace(/\n/g, '\r\n') : normalized;
}

export function cleanUnicodeText(value: string) {
  return value
    .replace(INVISIBLE_UNICODE_PATTERN, '')
    .replace(NON_BREAKING_SPACES_PATTERN, ' ')
    .normalize('NFKC');
}

export function cleanSmartQuotes(value: string) {
  return Array.from(value)
    .map((char) => SMART_QUOTES_MAP[char] ?? char)
    .join('');
}

export function createTextFixJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}
